export default class SpriteAnimationManager {
    constructor() {
        /**
         * @private
         * @type {number}
         */
        this.defaultFrameIncrement = 0;
        /**
         * @private
         * @type {number}
         */
        this.defaultElapsedFrameCount = 0;
        // /**
        //  * @private
        //  * @type {number}
        //  */
        // this.fastFrameIncrement = 0;


        /**
         * @constant
         * @private
         * @type {number}
         */
        this.defaultAnimationFramesPerFrame = 30;
        /**
         * @constant
         * @private
         * @type {number}
         */
        this.defaultMillisPerFrame = 150;
        /**
         * @constant
         * @private
         * @type {number}
         */
        this.slowMillisPerFrame = 250;
        /**
         * @constant
         * @private
         * @type {number}
         */
        this.fastMillisPerFrame = 80;
        /**
         * @constant
         * @private
         * @type {number}
         */
        this.superFastMillisPerFrame = 30;
    }

    /**
     * 
     * @param {number} frameTimeRatio 
     */
    updateForFrame(frameTimeRatio) {
        this.defaultElapsedFrameCount += frameTimeRatio;
        if (this.defaultElapsedFrameCount >= this.defaultAnimationFramesPerFrame) {
            this.defaultFrameIncrement++;
            this.defaultElapsedFrameCount = 0;

            // Reset the frame increment every once in a while.
            if (this.defaultFrameIncrement >= 50000) {
                this.defaultFrameIncrement = 0;
            }
        }
    }

    /**
     * @param {number} animationFrameCount The number of frames in the animated sprite.
     * @returns {number} The current frame index.
     */
    getDefaultFrameIndex(animationFrameCount) {
        return (this.defaultFrameIncrement % animationFrameCount) | 0;
    }

    /**
     * @param {number} currentTime 
     * @param {number} startTime The start time of the animation.
     * @param {number} animationFrameCount The number of frames in the animated sprite.
     * @param {boolean} looping
     * @returns {number} The current frame index, or -1 if not looping and we have completed the animation.
     */
    getFrameIndex(currentTime, startTime, animationFrameCount, looping) {
        return this.getFrameIndexInternal(currentTime, startTime, animationFrameCount, looping, this.defaultMillisPerFrame);
    }

    /**
     * @param {number} currentTime 
     * @param {number} startTime The start time of the animation.
     * @param {number} animationFrameCount The number of frames in the animated sprite.
     * @param {boolean} looping
     * @returns {number} The current frame index, or -1 if not looping and we have completed the animation.
     */    
    getFastFrameIndex(currentTime, startTime, animationFrameCount, looping) {
        return this.getFrameIndexInternal(currentTime, startTime, animationFrameCount, looping, this.fastMillisPerFrame);
    }

    /**
     * @param {number} currentTime 
     * @param {number} startTime The start time of the animation.
     * @param {number} animationFrameCount The number of frames in the animated sprite.
     * @param {boolean} looping
     * @returns {number} The current frame index, or -1 if not looping and we have completed the animation.
     */    
    getSlowFrameIndex(currentTime, startTime, animationFrameCount, looping) {
        return this.getFrameIndexInternal(currentTime, startTime, animationFrameCount, looping, this.slowMillisPerFrame);
    }

    /**
     * @param {number} currentTime 
     * @param {number} startTime The start time of the animation.
     * @param {number} animationFrameCount The number of frames in the animated sprite.
     * @param {boolean} looping
     * @returns {number} The current frame index, or -1 if not looping and we have completed the animation.
     */    
    getSuperFastFrameIndex(currentTime, startTime, animationFrameCount, looping) {
        return this.getFrameIndexInternal(currentTime, startTime, animationFrameCount, looping, this.superFastMillisPerFrame);
    }

    /**
     * @private
     * @param {number} currentTime 
     * @param {number} startTime The start time of the animation.
     * @param {number} animationFrameCount The number of frames in the animated sprite.
     * @param {boolean} looping
     * @param {number} millisPerFrame 
     * @returns {number} The current frame index, or -1 if not looping and we have completed the animation.
     */
    getFrameIndexInternal(currentTime, startTime, animationFrameCount, looping, millisPerFrame) {
        const frameCount = this.calculateElapsedFrameCount(currentTime, startTime, millisPerFrame);
        if (looping) {
            return (frameCount % animationFrameCount) | 0;
        }
        else if (frameCount < animationFrameCount) {
            return frameCount;
        }
        else {
            return -1;
        }
    }

    /**
     * @private
     * @param {number} currentTime 
     * @param {number} startTime 
     * @param {number} millisPerFrame 
     * @returns {number} The number of frames that have elapsed since the animation started.
     */
    calculateElapsedFrameCount(currentTime, startTime, millisPerFrame) {
        const elapsedMillis = Math.max(0, currentTime - startTime);
        return (elapsedMillis / millisPerFrame) | 0;
    }

    /**
     * @param {number} currentTime 
     * @param {number} startTime 
     * @param {number} animationFrameCount 
     * @returns {boolean}
     */
    isDefaultAnimationLoopCompleted(currentTime, startTime, animationFrameCount) {
        return this.calculateElapsedFrameCount(currentTime, startTime, this.defaultMillisPerFrame) >= animationFrameCount;
    }

    /**
     * @param {number} currentTime 
     * @param {number} startTime 
     * @param {number} animationFrameCount 
     * @returns {boolean}
     */
    isFastAnimationLoopCompleted(currentTime, startTime, animationFrameCount) {
        return this.calculateElapsedFrameCount(currentTime, startTime, this.fastMillisPerFrame) >= animationFrameCount;
    }

    /**
     * @param {number} currentTime 
     * @param {number} startTime 
     * @param {number} animationFrameCount 
     * @returns {boolean}
     */
    isSuperFastAnimationLoopCompleted(currentTime, startTime, animationFrameCount) {
        return this.calculateElapsedFrameCount(currentTime, startTime, this.superFastMillisPerFrame) >= animationFrameCount;
    }

}